import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ImageModule } from 'primeng/image';
import { MenuModule } from 'primeng/menu';
import { ToastModule } from 'primeng/toast';
import { CheckboxModule } from 'primeng/checkbox';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProjectPhotoModel, ProjectPhotoUnifiedModel } from '../../models/projectphoto.model';
import { ProjectphotoService } from '../../services/projectphoto.service';
import { ProjectService } from '../../services/project.service';
import { DialogModule } from 'primeng/dialog';
import { FileUploadModule } from 'primeng/fileupload';
import { MenuItem, MessageService } from 'primeng/api';
import { OciService } from '../../services/oci.service';
import { HttpResponse } from '@angular/common/http';
import { TableModule } from "primeng/table";
import { PhotoUploadService, UploadingPhoto } from '../../services/photo-upload.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ProjectStateService } from '../../services/project-state.service';
import { PermissionHelperService } from '../../services/permission-helper.service';
import { PAGE_KEYS, PERMISSION_KEYS } from '../../constants/permission.constants';
import { BlockScheduleModel } from '../../models/blockschedule.model';
import { ProjectModel } from '../../models/project.model';
import { BlockScheduleFileService } from '../../services/blockschedulefile.service';
import { buildProjectAssetDownloadFileName } from '../../utils/download-file-name.utils';
import { MediaFallbackDirective } from '../../shared/directives/media-fallback.directive';
import { isSupportedPhotoFile, SUPPORTED_PHOTO_ACCEPT } from '../../utils/image-file.utils';


@Component({
  selector: 'app-photos',
  imports: [CardModule, CommonModule, ImageModule, ToastModule, ButtonModule, MenuModule, DialogModule, FileUploadModule, CheckboxModule, TableModule, ProgressBarModule, MediaFallbackDirective],
  templateUrl: './photos.html',
  styleUrl: './photos.css',
})
export class PhotosComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('loadMoreSentinel')
  set loadMoreSentinel(element: ElementRef<HTMLElement> | undefined) {
    this.loadMoreSentinelElement = element;
    this.setupPhotoLoadObserver();
  }

  private loadMoreSentinelElement?: ElementRef<HTMLElement>;
  projectId: number = 0;
  projectPhotoModel: ProjectPhotoModel = {} as ProjectPhotoModel;
  photoList: ProjectPhotoModel[] = [];
  unifiedPhotoList: ProjectPhotoUnifiedModel[] = [];
  uploadingPhotos: UploadingPhoto[] = [];
  selectedPhotos: any[] = []
  selectedPhotoIds: number[] = [];
  showAddPhotoDialog: boolean = false;
  showAddPhotoFolderDialog: boolean = false;
  selectedPhotoForDownload: ProjectPhotoUnifiedModel | null = null;
  downloadingPhotoId: number | null = null;
  downloadingVariant: 'original' | 'optimized' | null = null;
  isBulkDownloading: boolean = false;
  private subscriptions: Subscription[] = [];
  sortBy: 'date' | 'skid' = 'date'; // Default sort by date
  isReadOnly: boolean = false;
  canView: boolean = false;
  canUpload: boolean = false;
  canDelete: boolean = false;
  isProjectInactive: boolean = false;
  private dataLoaded = false;
  projectInfo: ProjectModel = {} as ProjectModel;
  private blockScheduleMap = new Map<number, BlockScheduleModel>();
  readonly photoRenderBatchSize = 200;
  visiblePhotoCount = this.photoRenderBatchSize;
  private photoLoadObserver?: IntersectionObserver;
  actionItems: MenuItem[] = [];
  readonly supportedPhotoAccept = SUPPORTED_PHOTO_ACCEPT;

  readonly photoDownloadMenuItems: any[] = [
    {
      label: 'Original File',
      icon: 'pi pi-file',
      command: () => void this.downloadSelectedOriginalPhoto()
    },
    {
      label: 'Optimized File',
      icon: 'pi pi-image',
      command: () => void this.downloadSelectedOptimizedPhoto()
    }
  ];

  constructor(private route: ActivatedRoute,
    private router: Router,
    private _service: ProjectphotoService,
    private projectService: ProjectService,
    private messageService: MessageService,
    private ociService: OciService,
    private cdr: ChangeDetectorRef,
    private photoUploadService: PhotoUploadService,
    private projectStateService: ProjectStateService,
    private permissionHelper: PermissionHelperService,
    private blockScheduleFileService: BlockScheduleFileService,
  ) {
  }
  ngOnInit() {
    const projectSub = this.projectStateService.currentProject$.subscribe(project => {
      if (project && project.projectId) {
        this.projectInfo = project;
        this.cdr.markForCheck();
      }
    });
    this.subscriptions.push(projectSub);

    this.route.paramMap.subscribe(params => {
      const projectIdParam = params.get('projectId');
      if (projectIdParam) {
        this.projectId = parseInt(projectIdParam);
        console.log('Project ID from URL:', this.projectId);

        if (this.projectId > 0) {
          this.tryLoadPhotos();
          this.subscribeToUploads();
        } else {
          console.error('Invalid project ID:', this.projectId);
        }
      } else {
        console.error('No project ID found in URL parameters');
      }
    });

    const statusSub = this.projectStateService.currentProjectUserActive$.subscribe(isActive => {
      this.isProjectInactive = isActive === false;
      this.updateReadOnlyState();
    });
    this.subscriptions.push(statusSub);

    const permissionsSub = this.projectStateService.currentProjectPermissions$.subscribe(() => {
      this.canView = this.permissionHelper.has(PAGE_KEYS.PHOTOS, PERMISSION_KEYS.VIEW);
      this.canUpload = this.permissionHelper.has(PAGE_KEYS.PHOTOS, PERMISSION_KEYS.UPLOAD);
      this.canDelete = this.permissionHelper.has(PAGE_KEYS.PHOTOS, PERMISSION_KEYS.DELETE);
      this.updateReadOnlyState();
      this.updateActionItems();
      this.tryLoadPhotos();
      this.cdr.markForCheck();
    });
    this.subscriptions.push(permissionsSub);
  }

  ngAfterViewInit(): void {
    this.setupPhotoLoadObserver();
  }

  private updateReadOnlyState(): void {
    this.isReadOnly = this.isProjectInactive || (!this.canUpload && !this.canDelete);
    this.updateActionItems();
    this.cdr.markForCheck();
  }

  private updateActionItems(): void {
    const selectedCount = this.selectedPhotoIds.length;
    const items: MenuItem[] = [
      {
        label: 'Select All',
        icon: 'ri-check-line',
        disabled: this.unifiedPhotoList.length === 0 || selectedCount === this.unifiedPhotoList.length,
        command: () => this.selectAll()
      },
      {
        label: 'Deselect All',
        icon: 'ri-close-line',
        disabled: selectedCount === 0,
        command: () => this.deselectAll()
      },
      {
        label: 'Download',
        icon: 'ri-download-line',
        disabled: selectedCount === 0 || this.isBulkDownloading,
        command: () => void this.downloadSelectedPhotos()
      },
    ];

    if (this.canDelete) {
      items.push({ separator: true });
      items.push({
        label: 'Delete',
        icon: 'ri-delete-bin-line',
        styleClass: 'danger-menu-item',
        disabled: selectedCount === 0 || this.isBulkDownloading,
        command: () => this.deleteSelected()
      });
    }

    this.actionItems = items;
  }

  private tryLoadPhotos(): void {
    if (this.dataLoaded || !this.projectId || this.projectId <= 0) return;
    if (!this.canView) return;
    this.loadBlockSchedules();
    this.getProjectPhotos();
    this.dataLoaded = true;
  }

  private loadBlockSchedules(): void {
    this.projectService.getProjectBlockSchedule(this.projectId).subscribe({
      next: (data) => {
        const schedules = Array.isArray(data) ? data : [];
        this.blockScheduleMap = new Map(
          schedules
            .filter((item): item is BlockScheduleModel => !!item?.blockScheduleId)
            .map((item) => [item.blockScheduleId, item])
        );
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading block schedules for photo labels:', error);
        this.blockScheduleMap.clear();
      }
    });
  }

  ngOnDestroy() {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.photoLoadObserver?.disconnect();
  }

  private subscribeToUploads() {
    // Subscribe to upload queue to get uploading photos for this project
    const queueSub = this.photoUploadService.uploadQueue$.subscribe(queue => {
      this.uploadingPhotos = queue.filter(p => p.projectId === this.projectId);
      this.cdr.markForCheck();
    });
    this.subscriptions.push(queueSub);

    // Subscribe to photo uploaded events to refresh list when upload completes
    const uploadedSub = this.photoUploadService.photoUploaded$.subscribe(photo => {
      if (photo.projectId === this.projectId && photo.status === 'completed') {
        // Refresh the photo list to include the newly uploaded photo
        this.getProjectPhotos();
      }
    });
    this.subscriptions.push(uploadedSub);
  }
  getProjectPhotos() {
    this._service.getAllProjectPhotos(this.projectId).subscribe({
      next: (photos) => {
        this.unifiedPhotoList = photos;
        const availablePhotoIds = new Set(photos.map(photo => photo.photoId));
        this.selectedPhotoIds = this.selectedPhotoIds.filter(photoId => availablePhotoIds.has(photoId));
        this.visiblePhotoCount = Math.min(this.photoRenderBatchSize, this.unifiedPhotoList.length);
        console.log('Loaded unified project photos:', this.unifiedPhotoList);
        this.updateActionItems();
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error fetching project photos:', err);
      }
    });
  }

  getDownloadMenuItems(photo: ProjectPhotoModel) {
    return [
      {
        label: 'Original File',
        icon: 'pi pi-file',
        command: () => this.downloadOriginalFile(photo)
      },
      {
        label: 'Optimized File',
        icon: 'pi pi-image',
        command: () => this.downloadOptimizedFile(photo)
      }
    ];
  }

  downloadOriginalFile(photo: ProjectPhotoModel) {
    if (!photo.photoPath) return;

    this.ociService.getOciResourceEnhanced(photo.photoPath).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = buildProjectAssetDownloadFileName(this.projectInfo, 'Photo', photo.fileName || 'photo', {
          variant: 'Original',
          fallbackExtension: 'jpg',
        });
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error downloading original file:', err);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to download original file.' });
      }
    });
  }

  downloadOptimizedFile(photo: ProjectPhotoModel) {
    if (!photo.thumbnailPath) return;

    this.ociService.getOciResourceEnhanced(photo.thumbnailPath).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = buildProjectAssetDownloadFileName(this.projectInfo, 'Photo', photo.fileName || 'photo', {
          variant: 'Optimized',
          fallbackExtension: 'jpg',
        });
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error downloading optimized file:', err);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to download optimized file.' });
      }
    });
  }

  addPhoto() {
    if (!this.canUpload || this.isProjectInactive) {
      return;
    }
    if (!this.selectedPhotos || this.selectedPhotos.length === 0) {
      this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'No files selected.' });
      return;
    }

    // Add photos to upload queue for background processing
    this.photoUploadService.addPhotosToQueue(this.selectedPhotos, this.projectId);

    // Close dialog and clear selection
    this.showAddPhotoDialog = false;
    this.selectedPhotos = [];

    // Show success message
    this.messageService.add({
      severity: 'info',
      summary: 'Upload Started',
      detail: 'Photos are being uploaded in the background.'
    });
  }

  // FileUpload event handlers
  onFileSelect(event: any) {
    if (!this.canUpload || this.isProjectInactive) {
      return;
    }
    if (event && event.files) {
      const selectedFiles = Array.from(event.files) as File[];
      const supportedFiles = selectedFiles.filter(isSupportedPhotoFile);
      const rejectedCount = selectedFiles.length - supportedFiles.length;
      if (rejectedCount > 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Invalid Files',
          detail: `${rejectedCount} unsupported image file(s) were skipped.`
        });
      }
      this.selectedPhotos = [...this.selectedPhotos, ...supportedFiles];
    }
  }

  onFileRemove(event: any) {
    if (!this.canUpload || this.isProjectInactive) {
      return;
    }
    if (event && event.file) {
      this.selectedPhotos = this.selectedPhotos.filter(f => f !== event.file);
    }
  }

  onFileClear() {
    if (!this.canUpload || this.isProjectInactive) {
      return;
    }
    this.selectedPhotos = [];
  }

  openPhotoDownloadMenu(event: MouseEvent, photo: ProjectPhotoUnifiedModel, menu: any): void {
    event.stopPropagation();
    this.selectedPhotoForDownload = photo;
    menu.toggle(event);
  }

  async downloadSelectedOriginalPhoto(): Promise<void> {
    const photo = this.selectedPhotoForDownload;
    if (!photo) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Select a photo to download.' });
      return;
    }

    if (this.downloadingPhotoId) {
      return;
    }

    this.downloadingPhotoId = photo.photoId;
    this.downloadingVariant = 'original';
    try {
      await this.downloadOriginalPhoto(photo);
    } finally {
      this.downloadingPhotoId = null;
      this.downloadingVariant = null;
    }
  }

  async downloadSelectedOptimizedPhoto(): Promise<void> {
    const photo = this.selectedPhotoForDownload;
    if (!photo) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Select a photo to download.' });
      return;
    }

    if (this.downloadingPhotoId) {
      return;
    }

    this.downloadingPhotoId = photo.photoId;
    this.downloadingVariant = 'optimized';
    try {
      await this.downloadOptimizedPhoto(photo);
    } finally {
      this.downloadingPhotoId = null;
      this.downloadingVariant = null;
    }
  }

  async downloadSelectedPhotos(): Promise<void> {
    if (this.isBulkDownloading || this.selectedPhotoIds.length === 0) {
      return;
    }

    const selectedIdSet = new Set(this.selectedPhotoIds);
    const selectedPhotos = this.unifiedPhotoList.filter(photo => selectedIdSet.has(photo.photoId));
    if (selectedPhotos.length === 0) {
      this.messageService.add({ severity: 'warn', summary: 'No Photos Selected', detail: 'Select photos to download.' });
      return;
    }

    this.isBulkDownloading = true;
    this.updateActionItems();
    this.cdr.markForCheck();

    let successCount = 0;
    let failedCount = 0;

    try {
      for (const photo of selectedPhotos) {
        this.downloadingPhotoId = photo.photoId;
        this.downloadingVariant = 'original';
        this.cdr.markForCheck();

        const downloaded = await this.downloadOriginalPhoto(photo, false);
        if (downloaded) {
          successCount++;
        } else {
          failedCount++;
        }
      }
    } finally {
      this.downloadingPhotoId = null;
      this.downloadingVariant = null;
      this.isBulkDownloading = false;
      this.updateActionItems();
      this.cdr.markForCheck();
    }

    if (failedCount > 0 && successCount > 0) {
      this.messageService.add({ severity: 'warn', summary: 'Partial Download', detail: `${successCount} photo(s) downloaded, ${failedCount} failed.` });
    } else if (failedCount > 0) {
      this.messageService.add({ severity: 'error', summary: 'Download Failed', detail: `Failed to download ${failedCount} photo(s).` });
    } else {
      this.messageService.add({ severity: 'success', summary: 'Download Complete', detail: `${successCount} photo(s) downloaded.` });
    }
  }

  async downloadOriginalPhoto(photo: ProjectPhotoUnifiedModel, showErrorToast: boolean = true): Promise<boolean> {
    if (this.supportsAnnotatedDownloads(photo)) {
      return await this.downloadAnnotatedPhotoThroughApi(photo, false, showErrorToast);
    }

    if (!photo.photoPath) {
      if (showErrorToast) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Original file path is missing.' });
      }
      return false;
    }
    return await this.downloadPhotoWithMetadata(photo, photo.photoPath, photo.photoName || 'photo', showErrorToast);
  }

  async downloadOptimizedPhoto(photo: ProjectPhotoUnifiedModel, showErrorToast: boolean = true): Promise<boolean> {
    if (this.supportsAnnotatedDownloads(photo)) {
      return await this.downloadAnnotatedPhotoThroughApi(photo, true, showErrorToast);
    }

    if (!photo.thumbnailPath) {
      if (showErrorToast) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Optimized file path is missing.' });
      }
      return false;
    }
    return await this.downloadPhotoWithMetadata(photo, photo.thumbnailPath, photo.photoName || 'photo', showErrorToast);
  }

  private supportsAnnotatedDownloads(photo: ProjectPhotoUnifiedModel): boolean {
    return photo.sourceType === 'Project' || photo.sourceType === 'Skid';
  }

  private async downloadAnnotatedPhotoThroughApi(photo: ProjectPhotoUnifiedModel, optimized: boolean, showErrorToast: boolean = true): Promise<boolean> {
    try {
      const blob = photo.sourceType === 'Project'
        ? await firstValueFrom(this._service.downloadProjectPhoto(photo.photoId, optimized))
        : await firstValueFrom(this.blockScheduleFileService.downloadFile(photo.photoId, optimized));
      const stampedBlob = await this.createStampedPhotoBlob(photo, blob, photo.photoName || 'photo');
      const stampedFileName = this.getStampedFileName(photo, photo.photoName || 'photo', stampedBlob.type);
      this.triggerBlobDownload(stampedBlob, stampedFileName);
      return true;
    } catch (error) {
      console.error('Error downloading annotated photo:', error);
      if (showErrorToast) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to download photo.' });
      }
      return false;
    }
  }

  private async downloadPhotoWithMetadata(photo: ProjectPhotoUnifiedModel, filePath: string, fileName: string, showErrorToast: boolean = true): Promise<boolean> {
    try {
      const originalBlob = await firstValueFrom(this.ociService.getOciResourceEnhanced(filePath));
      const stampedBlob = await this.createStampedPhotoBlob(photo, originalBlob, fileName);
      const stampedFileName = this.getStampedFileName(photo, fileName, stampedBlob.type);
      this.triggerBlobDownload(stampedBlob, stampedFileName);
      return true;
    } catch (error) {
      console.error('Error downloading photo with metadata:', error);
      if (showErrorToast) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to download photo.' });
      }
      return false;
    }
  }

  private async createStampedPhotoBlob(photo: ProjectPhotoUnifiedModel, blob: Blob, fileName: string): Promise<Blob> {
    const targetMimeType = this.getCanvasOutputMimeType(fileName);
    const imageUrl = URL.createObjectURL(blob);

    try {
      const image = await this.loadImageElement(imageUrl);
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Canvas context is unavailable');
      }

      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      this.drawMetadataStamp(ctx, canvas.width, canvas.height, photo);

      return await this.canvasToBlob(canvas, targetMimeType);
    } catch (error) {
      console.warn('Falling back to raw download because metadata stamping failed:', error);
      return blob;
    } finally {
      URL.revokeObjectURL(imageUrl);
    }
  }

  private drawMetadataStamp(ctx: CanvasRenderingContext2D, width: number, height: number, photo: ProjectPhotoUnifiedModel): void {
    const lines = this.getMetadataStampLines(photo);
    if (lines.length === 0) {
      return;
    }

    const fontSize = Math.max(12, Math.round(width * 0.014));
    const lineHeight = Math.round(fontSize * 1.45);
    const padding = Math.max(12, Math.round(width * 0.012));
    const maxTextWidth = Math.min(width * 0.5, width - (padding * 2));

    ctx.save();
    ctx.font = `500 ${fontSize}px Inter, Arial, sans-serif`;

    const wrappedLines = lines.flatMap((line) => this.wrapText(ctx, line, maxTextWidth));
    const widestLine = wrappedLines.reduce((max, line) => Math.max(max, ctx.measureText(line).width), 0);
    const boxWidth = Math.min(maxTextWidth + (padding * 2), widestLine + (padding * 2));
    const boxHeight = (wrappedLines.length * lineHeight) + (padding * 2);
    const boxX = padding;
    const boxY = height - boxHeight - padding;

    ctx.fillStyle = 'rgba(9, 31, 45, 0.68)';
    ctx.fillRect(boxX, boxY, boxWidth, boxHeight);

    ctx.fillStyle = '#ffffff';
    ctx.textBaseline = 'top';

    wrappedLines.forEach((line, index) => {
      ctx.fillText(line, boxX + padding, boxY + padding + (index * lineHeight));
    });

    ctx.restore();
  }

  private getMetadataStampLines(photo: ProjectPhotoUnifiedModel): string[] {
    return [
      `Uploaded By: ${this.getUploadedByDisplay(photo)}`,
      `Captured At: ${this.getCapturedAtDisplay(photo)}`,
      `Latitude: ${this.getLatitudeDisplay(photo)}`,
      `Longitude: ${this.getLongitudeDisplay(photo)}`
    ];
  }

  private getParsedMetadata(photo: ProjectPhotoUnifiedModel): { capturedAt?: string; latitude?: number; longitude?: number } | null {
    if (!photo.metaData) {
      return null;
    }

    try {
      return JSON.parse(photo.metaData) as { capturedAt?: string; latitude?: number; longitude?: number };
    } catch (error) {
      console.warn('Unable to parse photo metadata:', error);
      return null;
    }
  }

  private getUploadedByDisplay(photo: ProjectPhotoUnifiedModel): string {
    const name = photo.createdByName?.trim();
    if (name) {
      return name;
    }

    if (photo.createdBy) {
      return `User #${photo.createdBy}`;
    }

    return 'Not available';
  }

  private getCapturedAtDisplay(photo: ProjectPhotoUnifiedModel): string {
    return this.getParsedMetadata(photo)?.capturedAt || 'Not available';
  }

  private getLatitudeDisplay(photo: ProjectPhotoUnifiedModel): string {
    const latitude = this.getParsedMetadata(photo)?.latitude;
    return typeof latitude === 'number' ? latitude.toFixed(6) : 'Not available';
  }

  private getLongitudeDisplay(photo: ProjectPhotoUnifiedModel): string {
    const longitude = this.getParsedMetadata(photo)?.longitude;
    return typeof longitude === 'number' ? longitude.toFixed(6) : 'Not available';
  }

  private wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      const trialLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(trialLine).width <= maxWidth || !currentLine) {
        currentLine = trialLine;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines;
  }

  private getCanvasOutputMimeType(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension === 'png') {
      return 'image/png';
    }
    if (extension === 'webp') {
      return 'image/webp';
    }
    return 'image/jpeg';
  }

  private getStampedFileName(photo: ProjectPhotoUnifiedModel, fileName: string, mimeType: string): string {
    const extension = mimeType === 'image/png'
      ? 'png'
      : mimeType === 'image/webp'
        ? 'webp'
        : 'jpg';

    const skidContext = photo.sourceType === 'Skid'
      ? this.blockScheduleMap.get(photo.blockScheduleId || 0)
      : null;

    return buildProjectAssetDownloadFileName(this.projectInfo, 'Photo', fileName, {
      skid: skidContext ? {
        tmeicSkidNo: skidContext.tmeicSkidNo,
        name: skidContext.name,
        blockName: skidContext.blockName,
        integratorSkidSerialNo: skidContext.integratorSkidSerialNo,
      } : photo.sourceType === 'Skid'
        ? {
          name: photo.skidNumber || null,
          integratorSkidSerialNo: photo.photoName || null,
        }
        : null,
      variant: 'Metadata',
      fallbackExtension: extension,
    });
  }

  private async loadImageElement(imageUrl: string): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Unable to load image for stamping'));
      image.src = imageUrl;
    });
  }

  private async canvasToBlob(canvas: HTMLCanvasElement, mimeType: string): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas returned an empty blob'));
          return;
        }
        resolve(blob);
      }, mimeType, 0.92);
    });
  }

  private triggerBlobDownload(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  openPhotoInViewer(photo: ProjectPhotoUnifiedModel): void {
    this.router.navigate(['/photos', this.projectId, 'view', photo.photoId], {
      state: {
        photoList: this.unifiedPhotoList,
        selectedPhotoId: photo.photoId
      }
    });
  }

  isPhotoSelected(photo: ProjectPhotoUnifiedModel): boolean {
    return this.selectedPhotoIds.includes(photo.photoId);
  }

  togglePhotoSelection(event: Event, photo: ProjectPhotoUnifiedModel): void {
    event.stopPropagation();
    const idx = this.selectedPhotoIds.indexOf(photo.photoId);
    if (idx === -1) {
      this.selectedPhotoIds = [...this.selectedPhotoIds, photo.photoId];
    } else {
      this.selectedPhotoIds = this.selectedPhotoIds.filter(id => id !== photo.photoId);
    }
    this.updateActionItems();
    this.cdr.markForCheck();
  }

  selectAll(): void {
    this.selectedPhotoIds = this.unifiedPhotoList.map(p => p.photoId);
    this.updateActionItems();
    this.cdr.markForCheck();
  }

  deselectAll(): void {
    this.selectedPhotoIds = [];
    this.updateActionItems();
    this.cdr.markForCheck();
  }

  deleteSelected() {
    if (!this.canDelete || this.isProjectInactive || this.selectedPhotoIds.length === 0) {
      return;
    }

    const selectedPhotos = this.unifiedPhotoList.filter(p => this.selectedPhotoIds.includes(p.photoId));
    const projectPhotoIds = selectedPhotos.filter(p => p.sourceType === 'Project').map(p => p.photoId);
    const skidPhotoIds = selectedPhotos.filter(p => p.sourceType === 'Skid').map(p => p.photoId);
    const count = this.selectedPhotoIds.length;

    this._service.bulkDeletePhotos(projectPhotoIds, skidPhotoIds).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: `${count} photo(s) deleted successfully.` });
        this.selectedPhotoIds = [];
        this.updateActionItems();
        this.getProjectPhotos();
      },
      error: (err) => {
        console.error('Error deleting photos:', err);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete photos.' });
      }
    });
  }

  get groupedPhotosByDate() {
    const groups = this.displayedPhotoList.reduce((groups: { [key: string]: ProjectPhotoUnifiedModel[] }, photo) => {
      const date = photo.createdDate ? new Date(photo.createdDate).toISOString().slice(0, 10) : 'Unknown';
      if (!groups[date]) groups[date] = [];
      groups[date].push(photo);
      return groups;
    }, {} as { [key: string]: ProjectPhotoUnifiedModel[] });

    // Sort photos within each date group descending (newest first)
    Object.keys(groups).forEach(date => {
      groups[date].sort((a, b) => {
        const ta = a.createdDate ? new Date(a.createdDate).getTime() : 0;
        const tb = b.createdDate ? new Date(b.createdDate).getTime() : 0;
        return tb - ta;
      });
    });

    return groups;
  }

  get groupedPhotoDates() {
    return Object.keys(this.groupedPhotosByDate).sort((a, b) => b.localeCompare(a)); // Descending order
  }

  get groupedPhotosBySkid() {
    const groups: { [key: string]: ProjectPhotoUnifiedModel[] } = {};

    // First, group MV Power Station photos by MV Power Station number
    this.displayedPhotoList.forEach(photo => {
      if (photo.sourceType === 'Skid') {
        const label = this.getPhotoSourceLabel(photo);
        if (!groups[label]) {
          groups[label] = [];
        }
        groups[label].push(photo);
      } else if (photo.sourceType === 'Project') {
        if (!groups['Project Photos']) {
          groups['Project Photos'] = [];
        }
        groups['Project Photos'].push(photo);
      }
    });

    return groups;
  }

  get groupedSkidKeys() {
    const keys = Object.keys(this.groupedPhotosBySkid);
    // Sort: MV Power Station photos first (by MV Power Station number), then Project Photos last
    return keys.sort((a, b) => {
      if (a === 'Project Photos') return 1;
      if (b === 'Project Photos') return -1;

      // Try sorting by trailing number in "<prefix> #<n>" patterns, else alphabetical.
      const numA = this.extractSkidNumber(a);
      const numB = this.extractSkidNumber(b);
      if (numA !== null && numB !== null) {
        return numA - numB;
      }
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    });
  }

  getPhotoSourceLabel(photo: ProjectPhotoUnifiedModel): string {
    if (photo.sourceType !== 'Skid') {
      return 'Project';
    }

    if (photo.blockScheduleId) {
      const block = this.blockScheduleMap.get(photo.blockScheduleId);
      if (block) {
        const customName = (block.name || block.blockName || '').trim();
        if (customName) {
          return customName;
        }
        return `${this.getEquipmentDisplayPrefix()} #${block.tmeicSkidNo ?? ''}`.trim();
      }
    }

    return (photo.skidNumber || '').trim() || this.getEquipmentDisplayPrefix();
  }

  private getEquipmentDisplayPrefix(): string {
    const configuredPrefix = (this.projectInfo?.prefix || '').trim();
    return !configuredPrefix || configuredPrefix.toLowerCase() === 'skid'
      ? 'MVPS'
      : configuredPrefix;
  }

  getPhotoSkidSubtitle(photo: ProjectPhotoUnifiedModel): string {
    if (photo.sourceType !== 'Skid' || !photo.blockScheduleId) {
      return '';
    }

    return (this.blockScheduleMap.get(photo.blockScheduleId)?.integratorSkidSerialNo || '').trim();
  }

  private extractSkidNumber(value: string): number | null {
    const match = value.match(/#\s*(\d+)/);
    if (!match) {
      return null;
    }

    const parsed = Number(match[1]);
    return Number.isFinite(parsed) ? parsed : null;
  }

  getPhotoUrl(photo: ProjectPhotoUnifiedModel): string {
    if (photo.thumbnailPath) {
      return `${environment.cdnBaseUrl}?key=${encodeURIComponent(photo.thumbnailPath)}`;
    }
    return '';
  }

  get displayedPhotoList(): ProjectPhotoUnifiedModel[] {
    return this.sortedPhotoList.slice(0, this.visiblePhotoCount);
  }

  get hiddenPhotoCount(): number {
    return Math.max(this.unifiedPhotoList.length - this.visiblePhotoCount, 0);
  }

  private get sortedPhotoList(): ProjectPhotoUnifiedModel[] {
    return [...this.unifiedPhotoList].sort((a, b) => {
      const timeA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
      const timeB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
      return timeB - timeA;
    });
  }

  private loadMorePhotos(): void {
    if (this.hiddenPhotoCount === 0) {
      return;
    }

    this.visiblePhotoCount = Math.min(
      this.visiblePhotoCount + this.photoRenderBatchSize,
      this.unifiedPhotoList.length
    );
    this.cdr.markForCheck();
  }

  private setupPhotoLoadObserver(): void {
    if (!this.loadMoreSentinelElement?.nativeElement || typeof IntersectionObserver === 'undefined') {
      return;
    }

    this.photoLoadObserver?.disconnect();
    this.photoLoadObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.loadMorePhotos();
        }
      },
      {
        root: null,
        rootMargin: '600px 0px',
        threshold: 0.01,
      }
    );
    this.photoLoadObserver.observe(this.loadMoreSentinelElement.nativeElement);
  }

  toggleSortBy() {
    this.sortBy = this.sortBy === 'date' ? 'skid' : 'date';
  }
}
